import { observer } from "mobx-react-lite";
import { FC, useMemo } from "react";
import styled from "@emotion/styled";
import { PlatformIconKind } from "@/domains/api/types";
import { css, cx } from "@/domains/emotion";
import { PlatformIcon } from "@/components/icons/platform-icon";
import { MDSIcon, MDSIconColor, MDSIconKind, useTheme } from "@/mds";

interface AppCalloutBannerIconProps {
  iconKind: PlatformIconKind | null;
  variant?: "illustration" | "inline";
}

export const AppCalloutBannerIcon: FC<AppCalloutBannerIconProps> = observer(
  ({ iconKind, variant = "illustration" }) => {
    const { theme } = useTheme();

    // const illustrationSrc =
    //   themeType === "harvest"
    //     ? HeadsUpEmptyStateIllustrationLight
    //     : HeadsUpEmptyStateIllustrationDark;

    const customStyles = useMemo(() => {
      if (!iconKind) {
        return undefined;
      }

      if (variant === "inline") {
        return css`
          height: 18px;
          width: 18px;
          color: ${iconKind === PlatformIconKind.Mem
            ? theme.colors.icon.brand
            : theme.colors.icon.primary};

          .mds-icon {
            color: ${iconKind === PlatformIconKind.Mem
              ? theme.colors.icon.brand
              : theme.colors.icon.primary};
            font-size: 18px;
            height: 18px;
            width: 18px;
          }
        `;
      }

      if (iconKind === PlatformIconKind.ThinkingHeads) {
        return css`
          height: 120px;
          width: 120px;
          color: ${theme.colors.background.banner.emphasis};
        `;
      }

      return css({
        height: "80px",
        width: "80px",
      });
    }, [
      iconKind,
      theme.colors.background.banner.emphasis,
      theme.colors.icon.brand,
      theme.colors.icon.primary,
      variant,
    ]);

    const innerIconStyles =
      variant === "inline"
        ? undefined
        : css({
            ".mds-icon": {
              fontSize: "58px",
              color: theme.colors.icon.primary,
            },
          });

    if (!iconKind) return null;

    if (variant === "inline" && iconKind === PlatformIconKind.Mem) {
      return (
        <IconWrapper variant={variant}>
          <MDSIcon kind={MDSIconKind.Logo} size={18} boxSize={18} color={MDSIconColor.Brand} />
        </IconWrapper>
      );
    }

    return (
      <IconWrapper variant={variant}>
        <PlatformIcon iconKind={iconKind} className={cx(customStyles, innerIconStyles)} />
      </IconWrapper>
    );
  }
);

const IconWrapper = styled.div<{ variant: "illustration" | "inline" }>(({ variant }) =>
  variant === "inline"
    ? {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        height: "18px",
        width: "18px",
      }
    : {
        paddingTop: "8px",
        paddingBottom: "8px",
        paddingLeft: "8px",
        paddingRight: "32px",
      }
);
